import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from './toast.service';

export interface DesktopNotificationOptions {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  ticketId?: string | number;
  data?: any;
  silent?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class DesktopNotificationService {
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);

  private readonly STORAGE_KEY_ENABLED = 'support_desk_desktop_notifications_enabled';
  private readonly STORAGE_KEY_SOUND = 'support_desk_notification_sound_enabled';

  readonly isSupported = signal<boolean>(typeof window !== 'undefined' && 'Notification' in window);
  readonly permission = signal<NotificationPermission>(this.getCurrentPermission());
  readonly isEnabled = signal<boolean>(this.getInitialEnabledState());
  readonly soundEnabled = signal<boolean>(this.getInitialSoundState());

  constructor() {
    if (this.isSupported()) {
      // Sync permission state
      this.permission.set(Notification.permission);
    }
  }

  private getCurrentPermission(): NotificationPermission {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return Notification.permission;
    }
    return 'denied';
  }

  private getInitialEnabledState(): boolean {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem(this.STORAGE_KEY_ENABLED);
    if (stored !== null) {
      return stored === 'true';
    }
    // Default to true if already granted
    return this.isSupported() && Notification.permission === 'granted';
  }

  private getInitialSoundState(): boolean {
    if (typeof window === 'undefined') return true;
    const stored = localStorage.getItem(this.STORAGE_KEY_SOUND);
    return stored !== null ? stored === 'true' : true;
  }

  /**
   * Request browser permission for desktop notifications.
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      this.toastService.warning(
        'Not Supported',
        'Desktop notifications are not supported by your current browser.'
      );
      return 'denied';
    }

    try {
      const result = await Notification.requestPermission();
      this.permission.set(result);

      if (result === 'granted') {
        this.setEnabled(true);
        this.toastService.success(
          'Notifications Enabled',
          'You will now receive desktop alerts for ticket updates.'
        );
        this.sendTestNotification('Desktop Notifications Activated', 'You will receive instant alerts on this device.');
      } else if (result === 'denied') {
        this.setEnabled(false);
        this.toastService.error(
          'Permission Denied',
          'Desktop notifications were blocked. Please enable them in your browser site settings.'
        );
      }
      return result;
    } catch (err) {
      console.error('Failed to request notification permission:', err);
      return 'denied';
    }
  }

  /**
   * Toggle enabled state or prompt for permissions if not yet granted.
   */
  async toggleDesktopNotifications(targetState?: boolean): Promise<boolean> {
    const nextState = targetState !== undefined ? targetState : !this.isEnabled();

    if (nextState) {
      if (!this.isSupported()) {
        this.toastService.warning('Not Supported', 'Desktop notifications are not supported in this browser.');
        return false;
      }

      if (Notification.permission === 'denied') {
        this.toastService.warning(
          'Notifications Blocked',
          'Please allow notifications in your browser address bar / site settings to enable this feature.'
        );
        this.setEnabled(false);
        return false;
      }

      if (Notification.permission === 'default') {
        const result = await this.requestPermission();
        return result === 'granted';
      }

      this.setEnabled(true);
      this.toastService.info('Desktop Alerts Enabled', 'Real-time OS notifications are active.');
      return true;
    } else {
      this.setEnabled(false);
      this.toastService.info('Desktop Alerts Disabled', 'Desktop notifications have been turned off.');
      return false;
    }
  }

  setEnabled(value: boolean): void {
    this.isEnabled.set(value);
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.STORAGE_KEY_ENABLED, String(value));
    }
  }

  toggleSound(targetState?: boolean): void {
    const nextState = targetState !== undefined ? targetState : !this.soundEnabled();
    this.soundEnabled.set(nextState);
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.STORAGE_KEY_SOUND, String(nextState));
    }
    if (nextState) {
      this.playChimeSound();
    }
  }

  /**
   * Send a desktop notification if permissions are granted and enabled.
   */
  sendNotification(options: DesktopNotificationOptions): Notification | null {
    if (!this.isSupported() || !this.isEnabled() || Notification.permission !== 'granted') {
      return null;
    }

    try {
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || '/favicon.ico',
        tag: options.tag,
        silent: options.silent || !this.soundEnabled(),
      });

      if (this.soundEnabled() && !options.silent) {
        this.playChimeSound();
      }

      notification.onclick = () => {
        try {
          window.focus();
        } catch {
          // ignore focus issues in some browsers
        }

        if (options.ticketId) {
          this.router.navigate(['/tickets/view', options.ticketId]);
        }
        notification.close();
      };

      // Auto close after 6 seconds
      setTimeout(() => {
        try {
          notification.close();
        } catch {
          // ignore
        }
      }, 6000);

      return notification;
    } catch (e) {
      console.warn('Desktop notification could not be created:', e);
      return null;
    }
  }

  /**
   * Trigger a test notification.
   */
  sendTestNotification(title = 'Support Desk - Test Alert', body = 'Real-time desktop notifications are functioning properly!'): void {
    if (Notification.permission !== 'granted') {
      this.requestPermission();
      return;
    }

    this.sendNotification({
      title,
      body,
      tag: 'test-notification',
    });
  }

  /**
   * Generates a sleek, pleasant chime sound using Web Audio API.
   */
  private playChimeSound(): void {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;

      const ctx = new AudioCtx();
      const now = ctx.currentTime;

      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();

      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(587.33, now); // D5
      osc1.frequency.exponentialRampToValueAtTime(880, now + 0.12); // A5

      osc2.type = 'triangle';
      osc2.frequency.setValueAtTime(880, now + 0.12); // A5
      osc2.frequency.exponentialRampToValueAtTime(1174.66, now + 0.25); // D6

      gain.gain.setValueAtTime(0.08, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(ctx.destination);

      osc1.start(now);
      osc1.stop(now + 0.15);
      osc2.start(now + 0.12);
      osc2.stop(now + 0.4);

      setTimeout(() => {
        try {
          ctx.close();
        } catch {}
      }, 500);
    } catch {
      // Audio context might be restricted before user gesture
    }
  }
}
